import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import EmailAddress from "_shared/email-addresses/email-address";
import Guid from "_shared/guids/guid";
import Name from "_shared/names/name";
import Individual from "marketplace/individuals/individual";
import UserCoworkerSchema, {
  UserCoworkerJSON,
  UserCoworkerParams,
} from "users/information/coworker-information/_schemas/user-coworker-schema";

export default class UserCoworker {
  public static create(values: UserCoworkerParams): UserCoworker {
    const parsed = UserCoworkerSchema.validate(values);
    if (isNullOrUndefined(parsed)) {
      throw new Error("Invalid UserCoworker parameters.");
    }
    return new UserCoworker(parsed);
  }

  public static fromJSON(json: UserCoworkerJSON): UserCoworker {
    if (isNullOrUndefined(json)) return json;
    const params = UserCoworkerSchema.deserialize(json);
    return new UserCoworker(params);
  }

  public static fromJSONArray(jsonArray: UserCoworkerJSON[]): UserCoworker[] {
    return jsonArray.map((json) => UserCoworker.fromJSON(json));
  }

  public readonly id?: Guid;
  public readonly legalEntityId: Guid;
  public readonly entityName: string;
  public readonly userId: Guid;
  public readonly email: EmailAddress;
  public readonly name: Name;
  public readonly individualId: Guid;
  public readonly avatarId?: Guid;
  /**
   * Whether their profile lists professional credentials, without which no team takes them as a
   * member (#5800). Undefined when the API did not say, which the pickers treat as not knowing.
   */
  public readonly hasCredentials?: boolean;

  public constructor(params: UserCoworkerParams) {
    const validatedParams = UserCoworkerSchema.validate(params);
    this.id = validatedParams.id;
    this.legalEntityId = validatedParams.legalEntityId;
    this.entityName = validatedParams.entityName;
    this.userId = validatedParams.userId;
    this.email = validatedParams.email;
    this.name = validatedParams.name;
    this.individualId = validatedParams.individualId;
    this.avatarId = validatedParams.avatarId;
    this.hasCredentials = validatedParams.hasCredentials;
  }

  /**
   * The coworker as the individual a picker hands back. `hasCredentials` does not come with it: the
   * individual carries the credentials themselves, which this record does not have, and a boolean
   * dressed up as profile text would read as their credentials to anything downstream. The pickers
   * decide on `hasCredentials` before they convert.
   */
  public asIndividual(): Individual {
    const id = this.individualId;
    const userId = this.userId;
    const legalEntityId = this.legalEntityId;
    const entityName = this.entityName;
    const firstName = this.name.firstName;
    const lastName = this.name.lastName;

    if (!firstName) throw new Error("First Name is required to convert UserCoworker to Individual");
    if (!lastName) throw new Error("Last Name is required to convert UserCoworker to Individual");

    return Individual.create({
      id,
      userId,
      isVisible: false,
      legalEntityId,
      profile: {
        firstName,
        lastName,
        avatarId: this.avatarId,
        entityName,
        email: this.email,
      },
    });
  }

  public toJSON(): UserCoworkerJSON {
    return UserCoworkerSchema.serialize(this);
  }

  public isEqualTo(other: UserCoworker): boolean {
    return this.toJSON() === other?.toJSON();
  }

  public toString(): string {
    return `UserCoworker (
      legalEntityId: ${this.legalEntityId.value},
      entityName: ${this.entityName},
      userId: ${this.userId.value},
      email: ${this.email.toString()},
      name: ${this.name.toString()},
      individualId: ${this.individualId.value},
      avatarId: ${this.avatarId?.value},
      hasCredentials: ${this.hasCredentials}
    )`;
  }
}
